import {memo, useCallback, useEffect, useState} from "react";
import {Row, Col, Tag, DatePicker, ConfigProvider} from "antd";
import dayjs from "dayjs";
import {apiError, handleRequest, numberFormat, renderDate} from "~/utils";
import {Image} from "~/components";
import {dashboardApi} from "~/api";
import FontAwesomeIcon from "~/components/FontAwesome";
import DashboardWidgetHeader from "./DashboardWidgetHeader";

/**
 * Xếp hạng hợp đồng giá trị cao nhất theo tháng cho Dashboard Kế toán trưởng,
 * gom theo 3 công ty (Sikido / D&H / Fast Digital).
 * Dữ liệu: dashboardApi.accountingContractRanks({time}) = { companies: [{key, name, items[]}] }.
 * Widget tự chọn tháng riêng, độc lập với bộ lọc chung của dashboard.
 */
//Màu & icon theo công ty (khớp Báo cáo tài chính)
const companyMeta = {
	skd: {color: '#0284c7', icon: 'fa-light fa-globe'},
	dh:  {color: '#7c3aed', icon: 'fa-light fa-buildings'},
	fd:  {color: '#e11d48', icon: 'fa-light fa-bullhorn'},
};
const rankColor = (index) => {
	if (index === 0) return '#f59e0b'; //vàng
	if (index === 1) return '#94a3b8'; //bạc
	if (index === 2) return '#b45309'; //đồng
	return '#cbd5e1';
};

function UserChip({user}) {
	if (!user || (!user.firstname && !user.lastname && !user.username)) return null;
	return (
		<div style={{display: 'flex', alignItems: 'center', gap: 6, minWidth: 0}}>
			<div style={{width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#e2e8f0'}}>
				<Image src={user.avatar} />
			</div>
			<span style={{fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
				{user.firstname} {user.lastname}
			</span>
		</div>
	);
}

function RankItem({item, index}) {
	return (
		<div style={{
			display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px dashed #eef2f7',
		}}>
			<div style={{
				width: 22, height: 22, borderRadius: 6, flexShrink: 0,
				background: rankColor(index), color: '#fff',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				fontSize: 12, fontWeight: 700,
			}}>{index + 1}</div>
			<div style={{flex: 1, minWidth: 0}}>
				<div style={{display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline'}}>
					<b style={{fontSize: 12, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
						{item.code} {item.name}
					</b>
					<b style={{fontSize: 13, color: '#059669', flexShrink: 0}}>{numberFormat(item.price)}</b>
				</div>
				<div style={{display: 'flex', flexWrap: 'wrap', gap: 4, margin: '4px 0'}}>
					{item.service?.label && <Tag color={item.service?.badge} style={{margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 5px'}}>{item.service.label}</Tag>}
					{item.serviceType?.label && <Tag color={item.service?.badge} style={{margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 5px'}}>{item.serviceType.label}</Tag>}
					{item.package && <Tag color={item.service?.badge} style={{margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 5px'}}>{item.package}</Tag>}
				</div>
				<div style={{display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center'}}>
					<UserChip user={item.user} />
					<span style={{fontSize: 10, color: '#94a3b8', flexShrink: 0}}>{renderDate(item.signing)}</span>
				</div>
				{item.userShare && (item.userShare.firstname || item.userShare.username) && (
					<div style={{marginTop: 4}}><UserChip user={item.userShare} /></div>
				)}
			</div>
		</div>
	);
}

function CompanyColumn({company}) {

	const meta   = companyMeta[company.key] || {color: '#7c3aed', icon: 'fa-light fa-building'};
	const groups = company.groups || [];
	const showGroupLabel = groups.length > 1; //Sikido/D&H có Website+Host; Fast Digital chỉ 1 nhóm

	return (
		<div>
			<div style={{
				display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
				paddingBottom: 6, borderBottom: `2px solid ${meta.color}`,
			}}>
				<FontAwesomeIcon icon={meta.icon} style={{color: meta.color}} />
				<span style={{fontSize: 14, fontWeight: 700, color: '#334155'}}>{company.name}</span>
			</div>

			{groups.map((group) => {
				const items = group.items || [];
				return (
					<div key={`grp-${company.key}-${group.key}`} style={{marginBottom: showGroupLabel ? 10 : 0}}>
						{showGroupLabel && (
							<div style={{
								display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0 2px',
								fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3,
							}}>
								<span style={{width: 6, height: 6, borderRadius: '50%', background: meta.color}} />
								{group.name}
								<span style={{color: '#cbd5e1', fontWeight: 600}}>({items.length})</span>
							</div>
						)}
						{items.length === 0
							? <p style={{fontSize: 12, color: '#94a3b8', padding: '6px 0', margin: 0}}>Không có hợp đồng</p>
							: items.map((item, index) => <RankItem key={`rank-${item.id}`} item={item} index={index} />)
						}
					</div>
				);
			})}
		</div>
	);
}

function AccountingContractRankWidget() {

	const [time, setTime] = useState(dayjs().startOf('month'));

	const [loading, setLoading] = useState(true);

	const [companies, setCompanies] = useState([]);

	const fetchData = useCallback(async (t) => {
		setLoading(true);
		const [error, response] = await handleRequest(dashboardApi.accountingContractRanks({time: t.startOf('month').unix()}));
		if (!apiError('', error, response)) {
			setCompanies(response.data?.companies || []);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchData(time).then();
	}, [time, fetchData]);

	const monthPicker = (
		<ConfigProvider theme={{token: {colorBgContainer: 'rgba(255,255,255,0.18)', colorText: '#fff', colorTextPlaceholder: 'rgba(255,255,255,0.7)', colorIcon: '#fff', colorBorder: 'transparent'}}}>
			<DatePicker
				picker="month"
				allowClear={false}
				size="small"
				format="[Th] M / YYYY"
				value={time}
				onChange={(d) => d && setTime(d.startOf('month'))}
				suffixIcon={<FontAwesomeIcon icon="fa-light fa-calendar" style={{color: '#fff'}} />}
				style={{width: 130}}
			/>
		</ConfigProvider>
	);

	return (
		<div style={{borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(124,58,237,0.12)', background: '#fff'}}>
			<DashboardWidgetHeader
				icon="fa-light fa-trophy"
				title="Xếp hạng hợp đồng theo công ty"
				subtitle="Hợp đồng giá trị cao nhất trong tháng"
				gradient="linear-gradient(135deg, #6d28d9 0%, #7c3aed 60%, #a78bfa 100%)"
				extra={monthPicker}
			/>
			<div style={{padding: '14px 18px'}}>
				{loading
					? <div style={{textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '16px 0'}}>Đang tải...</div>
					: (
						<Row gutter={[24, 16]}>
							{companies.map((company) => (
								<Col xs={24} md={8} key={`rank-company-${company.key}`}>
									<CompanyColumn company={company} />
								</Col>
							))}
						</Row>
					)
				}
			</div>
		</div>
	);
}

export default memo(AccountingContractRankWidget);
